import { ChatConversation } from '@/components/chat/conversation';
import { ChatEnding } from '@/components/chat/ending';
import { ChatExpired } from '@/components/chat/expired';
import { ChatLobby } from '@/components/chat/lobby';
import { ChatUnavailable } from '@/components/chat/unavailable';
import { getChatRoom } from '@/lib/api';
import { chatLobbyRefetchInterval, chatRoomQueryKey } from '@/lib/chat-lobby';
import { chatRoomPhase, isChatLinkGone } from '@/lib/chat-phase';
import { assertNever } from '@matter_extractor/shared';
import { Spinner } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/chat/$token')({
  component: ChatPage,
});

/**
 * The blind interview, all ten screens of it (chat spec §3).
 *
 * A SHELL, and the only thing it decides is WHICH PHASE is on screen. The
 * components below are the phases, one per file, filled by four lots landing in
 * parallel - so the routing lives here once instead of in each of them, and the
 * DECISION lives in `lib/chat-phase.ts`, pure and tested, rather than in a
 * component nothing can render without a router.
 *
 * `/chat/<jeton>` is ONE MORE TanStack route and nothing else (§3): the SPA is
 * already served at the root of the domain, so there is no path prefix, no Vite
 * `base`, no proxy rule and no domain to wire. The token sits in the PATH and
 * never in the query - a secret in a query string leaks through the `Referer`,
 * the history and the proxy logs (§5).
 *
 * The role is NEVER read from the URL. It arrives inside `room.role`, resolved
 * server-side from the token, which is what keeps the closing control out of
 * the guest's DOM rather than merely hidden in it (§3, §5).
 *
 * **The shell is also the poller** (§7, #8). One query, one key: the lobby
 * needs the room every ~1500 ms, and the room read is ALSO the presence stamp
 * on the server, so a second query beside this one would have been a second
 * « I am here » and twice the traffic on the only screen of the product that
 * polls. The cadence itself - including when it stops - is
 * `chatLobbyRefetchInterval`, pure and tested.
 */
function ChatPage() {
  const { token } = Route.useParams();
  const room = useQuery({
    queryKey: chatRoomQueryKey(token),
    queryFn: () => getChatRoom(token),
    // No automatic retry: the refusal this call actually makes is a 410, and a
    // 410 is an ANSWER (§5). Retrying it would delay S8 by three round trips.
    // Everything that is NOT a 410 gets a retry the person fires herself, on
    // the screen below - which is the difference between a dead link and a bad
    // minute on the train.
    retry: false,
    // Read off the answer in hand, so the poll stops the moment there is
    // nothing left to wait for: past the lobby the socket carries the
    // conversation, and past the entry window every call would answer 410.
    refetchInterval: (query) =>
      chatLobbyRefetchInterval({
        status: query.state.data?.status,
        expiresAt: query.state.data?.expiresAt,
        now: Date.now(),
      }),
  });

  if (room.isPending) {
    return <Spinner label="Ouverture de la salle" />;
  }

  // ONLY a 410 renders the dead-link screen. A 429 from the per-address
  // ceiling, a dropped connection or a schema parse that failed are not a dead
  // link, and §4.5 forbids sending a live interview backwards for any of them.
  //
  // Checked BEFORE the data in hand, and that ordering is #8's: with a poll
  // running, `data` survives the error that follows it, so a room whose window
  // closed under an open lobby would have gone on rendering that lobby for as
  // long as the tab stayed open. A 410 is never answered for a room in
  // `chatting`, so this cannot pull a live conversation backwards.
  if (isChatLinkGone(room.error)) return <ChatExpired />;
  if (room.data === undefined) {
    return <ChatUnavailable onRetry={() => void room.refetch()} retrying={room.isFetching} />;
  }

  const phase = chatRoomPhase(room.data.status);
  switch (phase) {
    case 'lobby':
      return <ChatLobby token={token} room={room.data} />;
    case 'conversation':
      return <ChatConversation room={room.data} />;
    case 'ending':
      return <ChatEnding room={room.data} />;
    default:
      return assertNever(phase, 'chat phase');
  }
}
